import { FC, useState } from "react";

import { ButtonGroup, Card, ToggleButton } from "react-bootstrap";

import { FileText } from "lucide-react";

import { useGetFilteredQuotes } from "@/_services/hooks/invoice.hooks";
import { Quote } from "@/_services/invoiceService";

import ExistingQuoteSelection from "./ExistingQuoteSelection";
import ProductCatalog from "./ProductCatalog";
import { useQuoteAssociation } from "../hooks/useQuoteAssociation";

interface Props {
  onQuoteSelection?: (quoteId: string, selectedQuote: Quote | undefined) => void;
}

const QuoteSelectionCard: FC<Props> = ({ onQuoteSelection }) => {
  const { 
    quoteOption, 
    setValue 
  } = useQuoteAssociation();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("estimate_pending_status");
  
  const {
    data: quotesData,
    isLoading: loadingQuotes,
  } = useGetFilteredQuotes(filterStatus);

  const quotes = quotesData?.estimates || [];
  const filteredQuotes = quotes.filter(
    (quote) =>
      quote.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.estimate_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleQuoteOptionChange = (option: string) => {
    setValue("quoteOption", option);
    if (option === "new") {
      setValue("selectedQuote", "");
    }
  };

  const handleQuoteSelection = (quoteId: string) => {
    setValue("selectedQuote", quoteId);
    
    const selectedQuote = quotes.find(quote => quote.id === quoteId);
    onQuoteSelection?.(quoteId, selectedQuote);
  };

  const formatQuoteStatus = (status: string) => {
    switch (status) {
      case "pending":
        return "En attente";
      case "accepted":
        return "Validé";
      case "draft":
        return "Brouillon";
      case "invoiced": 
        return "Facturé";
      default:
        return status;
    }
  };

  const formatAmount = (amount: string, currency: string = "EUR") => {
    return `${parseFloat(amount).toFixed(2)} ${currency}`;
  };
  return (
    <Card className="border-0 shadow-sm bg-white">
      <Card.Header>
        <Card.Title className="flex items-center space-x-2">
          <FileText className="w-4 h-4 text-purple-600" />
          <span className="text-sm">Gestion du devis</span>
        </Card.Title>
      </Card.Header>
      <Card.Body className="space-y-4">
        <ButtonGroup>
          <ToggleButton
            id="existing-quote"
            type="radio"
            variant="outline-primary"
            checked={quoteOption === "existing"}
            onClick={() => handleQuoteOptionChange("existing")}
            value="existing"
          >
            Associer à un devis existant
          </ToggleButton>
          <ToggleButton
            id="new-quote"
            type="radio"
            variant="outline-primary"
            checked={quoteOption === "new"}
            onClick={() => handleQuoteOptionChange("new")}
            value="new"
          >
            Créer un nouveau devis
          </ToggleButton>
        </ButtonGroup>

        <ExistingQuoteSelection
          quoteOption={quoteOption}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          loadingQuotes={loadingQuotes}
          filteredQuotes={filteredQuotes}
          onQuoteSelection={handleQuoteSelection}
          formatQuoteStatus={formatQuoteStatus}
          formatAmount={formatAmount}
          setFilterStatus={setFilterStatus}
          filterStatus={filterStatus}
        />

        <ProductCatalog />
      </Card.Body>
    </Card>
  );
};

export default QuoteSelectionCard;
